import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  GroupedSuppliesTableRow,
  GroupedSuppliesTableRowKeys,
  SuppliesGroupedTableColumns,
  SupplyCupGroupedContract,
} from "../../../core/models/supplies/supplies.model";
import "./SuppliesTable.scss";
import { useNavigate } from "react-router-dom";
import { TableSortLabel } from "@mui/material";
import { SuppliesOrderState } from "../Supplies";
import SupplyContractsTable from "../SupplyContractsTable/SupplyContractsTable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCallback, useState } from "react";
import { setSupplySelectedToLocalStorage } from "../../../core/services/localStorage/localStorage.service";
import { isEmpty, isNull } from "lodash";

interface SuppliesTableProps {
  suppliesTableRows: GroupedSuppliesTableRow[];
  isAdministrator: boolean | undefined;
  orderState: SuppliesOrderState;
  setOrderState: React.Dispatch<React.SetStateAction<SuppliesOrderState>>;
}

const SuppliesTable = ({
  suppliesTableRows,
  isAdministrator,
  orderState,
  setOrderState,
}: SuppliesTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const suppliesTableColumns: SuppliesGroupedTableColumns[] = [
    ...(isAdministrator
      ? [
          {
            id: "socialReason" as GroupedSuppliesTableRowKeys,
            label: "Razón social",
            align: "left",
            width: "10%",
          },
          {
            id: "cif" as GroupedSuppliesTableRowKeys,
            label: "CIF",
            align: "left",
            width: "3%",
          },
        ]
      : []),
    {
      id: "suppliesDirection" as GroupedSuppliesTableRowKeys,
      label: "Dirección de suministro",
      align: "left",
      width: "20%",
    },
    {
      id: "town" as GroupedSuppliesTableRowKeys,
      label: "Localidad",
      align: "left",
      width: "8%",
    },
    {
      id: "billsNumber" as GroupedSuppliesTableRowKeys,
      label: "Facturas",
      align: "left",
      width: "8%",
      cellContent: (row: GroupedSuppliesTableRow) => (
        <Link
          component="button"
          onClick={() => {
            setSupplySelectedToLocalStorage(row);
            navigate(
              `${process.env.REACT_APP_BILLS}/${row.cupsId}?origin=supplies`
            );
          }}
          underline="always"
        >{`${row.billsNumber} ${t("Tablas.Facturas")}`}</Link>
      ),
    },
    {
      id: "CUPS" as GroupedSuppliesTableRowKeys,
      label: "CUPS",
      align: "left",
      width: "20%",
      cellContent: (row: GroupedSuppliesTableRow) => (
        <Link
          component="button"
          onClick={() => {
            setSupplySelectedToLocalStorage(row);
            navigate(
              `${process.env.REACT_APP_SUPPLIES}/${row.cupsId}?origin=supplies`
            );
          }}
          underline="always"
        >
          {row.CUPS}
        </Link>
      ),
    },
  ];

  const createSortHandler =
    (property: keyof GroupedSuppliesTableRow) =>
    (event: React.MouseEvent<unknown>) => {
      event.preventDefault();
      const isAsc =
        orderState.orderBy === property && orderState.order === "asc";
      setOrderState({
        order: orderState.orderBy !== undefined && isAsc ? "desc" : "asc",
        orderBy: property,
      });
    };

  const columnsWithSorting: (keyof GroupedSuppliesTableRow)[] = [
    "CUPS",
    "town",
    // "cif", // might be needed in the future
    // "socialReason", // might be needed in the future
  ];

  const getSupplyActiveClass = (
    supplyContracts: SupplyCupGroupedContract[]
  ): string => {
    if (
      isEmpty(supplyContracts) ||
      supplyContracts.every(
        ({ FechaBaja }) => !isNull(FechaBaja) && !isEmpty(FechaBaja)
      )
    ) {
      return "inactive";
    }
    return "";
  };

  return (
    <TableContainer className="supplies-table">
      <Table>
        <TableHead>
          <TableRow>
            {suppliesTableColumns.map(({ id, align, width, label }) => {
              const isSortable = columnsWithSorting.includes(
                id as keyof GroupedSuppliesTableRow
              );
              const ColumnLabel = () => (
                <Typography>{t(`Tablas.${label}`)}</Typography>
              );

              if (!isSortable) {
                return (
                  <TableCell key={id} align={align} width={width}>
                    <ColumnLabel />
                  </TableCell>
                );
              } else {
                const direction =
                  orderState.orderBy === id ? orderState.order : "asc";
                const isOrdered = orderState.orderBy === id;
                return (
                  <TableCell key={id} align={align} width={width}>
                    <TableSortLabel
                      active={isOrdered}
                      direction={direction}
                      onClick={createSortHandler(
                        id as keyof GroupedSuppliesTableRow
                      )}
                      IconComponent={(props) => (
                        <Typography
                          {...props}
                          component={"span"}
                          id={orderState.orderBy}
                          className={
                            orderState.order === "asc" && isOrdered
                              ? "t-icon-arrow-top"
                              : "t-icon-arrow-down"
                          }
                          sx={{
                            color: "primary.main",
                            marginLeft: ".4rem",
                            fontSize: "20px",
                          }}
                        />
                      )}
                    >
                      <Typography>{t(`Tablas.${label}`)}</Typography>
                    </TableSortLabel>
                  </TableCell>
                );
              }
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {!!suppliesTableRows.length &&
            suppliesTableRows.map((row, indexNumber) => {
              const TableRowElement = () => (
                <TableRow
                  key={`${row.id}_${indexNumber}`}
                  sx={{ width: "100%" }}
                  className={`table-row ${getSupplyActiveClass(row.contracts)}`}
                >
                  {suppliesTableColumns.map(
                    ({ id, width, align, cellContent }) => (
                      <TableCell
                        key={`${row.id}_${id}`}
                        align={align}
                        width={width}
                      >
                        {cellContent
                          ? cellContent(row)
                          : (row[
                              id as keyof GroupedSuppliesTableRow
                            ] as string)}
                      </TableCell>
                    )
                  )}
                </TableRow>
              );

              if (row.contracts.length > 0) {
                const isExpanded = expandedRow === row.id;
                return (
                  <Accordion
                    elevation={0}
                    expanded={isExpanded}
                    onChange={(event) => {
                      const isExpanding = expandedRow !== row.id;
                      const currentTarget = event.currentTarget;
                      setExpandedRow(expandedRow === row.id ? null : row.id);
                      if (isExpanding) {
                        setTimeout(() => {
                          if (currentTarget) {
                            const rect = currentTarget.getBoundingClientRect();
                            if (rect.top < 0) {
                              currentTarget.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }
                        }, 300);
                      }
                    }}
                  >
                    <AccordionSummary>
                      <TableRow
                        key={`${row.id}_${indexNumber}`}
                        sx={{ width: "100%" }}
                        className={`table-row ${getSupplyActiveClass(
                          row.contracts
                        )}`}
                      >
                        {suppliesTableColumns.map(
                          ({ id, width, align, cellContent }) => (
                            <TableCell
                              key={`${row.id}_${id}`}
                              align={align}
                              width={width}
                            >
                              {cellContent
                                ? cellContent(row)
                                : (row[
                                    id as keyof GroupedSuppliesTableRow
                                  ] as string)}
                            </TableCell>
                          )
                        )}
                        <ExpandMoreIcon
                          className={`expand_icon${
                            isExpanded ? " expanded" : ""
                          }`}
                          fontSize="medium"
                        />
                      </TableRow>
                    </AccordionSummary>
                    <AccordionDetails sx={{ paddingX: 4 }}>
                      <SupplyContractsTable contractList={row.contracts} />
                    </AccordionDetails>
                  </Accordion>
                );
              } else {
                return <TableRowElement />;
              }
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SuppliesTable;
